import { OPENAI_API_KEY, API_URLS } from "../config/api";

/**
 * Format structured analysis from JSON
 */
export function formatStructuredAnalysis(analysisJson) {
  let formatted = "";

  formatted += "=".repeat(70) + "\n";
  formatted += "ANALYSIS RESULTS\n";
  formatted += "=".repeat(70) + "\n";

  const devSuit = analysisJson.developmentSuitability || {};
  formatted += `\n📊 Development Suitability: ${devSuit.score || "N/A"}/10 - ${
    devSuit.level || "N/A"
  }\n`;

  const dashboard = analysisJson.dashboardSummary || {};
  formatted += `🎯 Overall Readiness: ${
    dashboard.overallReadiness || "N/A"
  }%\n`;
  formatted += `⚠️  Critical Issues: ${dashboard.criticalIssues || "N/A"}\n`;
  formatted += `📋 Action Items: ${dashboard.actionItemsNeeded || "N/A"}\n`;

  const dataComp = analysisJson.dataCompleteness || {};
  formatted += `📊 Data Completeness: ${dataComp.overall || "N/A"}%\n`;

  const risks = analysisJson.risks || [];
  if (risks.length > 0) {
    formatted += `\n🚨 Top Risks:\n`;
    for (let i = 0; i < Math.min(3, risks.length); i++) {
      const risk = risks[i];
      formatted += `   • ${risk.category || "Unknown"}: ${
        risk.severity || "unknown"
      } severity - ${(risk.impact || "").substring(0, 60)}...\n`;
    }
  }

  const recommendations = analysisJson.recommendations || [];
  if (recommendations.length > 0) {
    formatted += `\n💡 Top Recommendations:\n`;
    for (let i = 0; i < Math.min(3, recommendations.length); i++) {
      const rec = recommendations[i];
      formatted += `   • [${(rec.priority || "unknown").toUpperCase()}] ${(
        rec.action || ""
      ).substring(0, 60)}...\n`;
    }
  }

  formatted += "\n" + "=".repeat(70) + "\n";

  return formatted;
}

/**
 * Analyze data using GPT-4o-mini
 */
export async function analyzeWithGPT(data, conversationHistory) {
  if (!OPENAI_API_KEY) {
    throw new Error("OpenAI API key not configured");
  }

  try {
    const messages = [
      {
        role: "system",
        content:
          "You are an urban planning analysis AI. Analyze the provided environmental data and return ONLY a valid JSON object (no markdown, no explanations, no greetings).",
      },
      {
        role: "user",
        content: `Analyze this comprehensive area data and return ONLY a valid JSON object with the following structure:

Required JSON structure:

{
  "developmentSuitability": {
    "score": <number 0-10>,
    "maxScore": 10,
    "level": "<poor|fair|moderate|good|excellent>",
    "summary": "<brief 1-2 sentence assessment>",
    "factors": [
      {"name": "<factor name>", "score": <0-10>, "impact": "<low|medium|high>"}
    ]
  },
  "risks": [
    {
      "category": "<risk type>",
      "severity": "<low|medium|high|critical>",
      "probability": "<low|medium|high>",
      "impact": "<description of impact>",
      "mitigationPriority": <1-10>,
      "timeframe": "<immediate|short-term|medium-term|long-term>"
    }
  ],
  "keyFindings": [
    {
      "finding": "<concise finding>",
      "confidence": <0.0-1.0>,
      "dataQuality": "<low|medium|high>",
      "category": "<environmental|infrastructure|data_gap|opportunity>"
    }
  ],
  "recommendations": [
    {
      "action": "<recommended action>",
      "priority": "<low|medium|high|critical>",
      "effort": "<low|medium|high>",
      "cost": "<$|$$|$$$|$$$$>",
      "timeframe": "<duration estimate>",
      "impact": "<expected outcome>",
      "category": "<assessment|infrastructure|planning|environmental>"
    }
  ],
  "metrics": {
    "temperature": {
      "current": <average temp>,
      "min": <min temp>,
      "max": <max temp>,
      "trend": "<rising|stable|falling>"
    },
    "precipitation": {
      "total": <total mm>,
      "average": <avg mm/day>,
      "trend": "<increasing|stable|decreasing>"
    },
    "humidity": {
      "average": <percentage>,
      "assessment": "<low|moderate|high>"
    }
  },
  "dataCompleteness": {
    "overall": <percentage 0-100>,
    "categories": [
      {"name": "<category>", "completeness": <0-100>, "critical": <boolean>}
    ]
  },
  "dashboardSummary": {
    "overallReadiness": <percentage 0-100>,
    "criticalIssues": <count>,
    "actionItemsNeeded": <count>,
    "dataGaps": <count>
  }
}

Note: The analysis covers the area bounded by coordinates:
- North: ${
          data.areas[0]?.environmental_data?.bounding_box?.north?.toFixed(6) ||
          "N/A"
        }
- South: ${
          data.areas[0]?.environmental_data?.bounding_box?.south?.toFixed(6) ||
          "N/A"
        }
- East: ${
          data.areas[0]?.environmental_data?.bounding_box?.east?.toFixed(6) ||
          "N/A"
        }
- West: ${
          data.areas[0]?.environmental_data?.bounding_box?.west?.toFixed(6) ||
          "N/A"
        }

Base your analysis strictly on the provided data. Assess soil properties, weather patterns, risks, and development feasibility. Return ONLY the JSON object.

Data:
${JSON.stringify(data, null, 2)}`,
      },
    ];

    const response = await fetch(API_URLS.OPENAI, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${OPENAI_API_KEY}`,
      },
      body: JSON.stringify({
        model: "gpt-4o-mini",
        messages: messages,
        temperature: 0.7,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.error?.message || `API Error: ${response.status}`
      );
    }

    const result = await response.json();
    const rawAnalysis = result.choices[0].message.content;

    let cleanResponse = rawAnalysis.trim();
    if (cleanResponse.startsWith("```json")) {
      cleanResponse = cleanResponse.substring(7);
    }
    if (cleanResponse.startsWith("```")) {
      cleanResponse = cleanResponse.substring(3);
    }
    if (cleanResponse.endsWith("```")) {
      cleanResponse = cleanResponse.substring(0, cleanResponse.length - 3);
    }
    cleanResponse = cleanResponse.trim();

    let analysisJson = null;
    let formattedAnalysis = rawAnalysis;

    try {
      analysisJson = JSON.parse(cleanResponse);
      formattedAnalysis = formatStructuredAnalysis(analysisJson);
    } catch (parseError) {
      console.error("JSON parsing failed:", parseError);
      console.log("Raw response:", rawAnalysis);
      formattedAnalysis = `⚠️ Could not parse JSON response: ${parseError.message}\n\nRaw response:\n${rawAnalysis}`;
    }

    const updatedHistory = [
      ...conversationHistory,
      ...messages,
      {
        role: "assistant",
        content: rawAnalysis,
      },
    ];

    return {
      formatted: formattedAnalysis,
      json: analysisJson,
      raw: rawAnalysis,
      conversationHistory: updatedHistory,
    };
  } catch (error) {
    console.error("GPT API Error:", error);
    throw error;
  }
}

/**
 * Ask GPT a question for chatbot
 */
export async function askGPT(query, conversationHistory, analysisData = null) {
  if (!OPENAI_API_KEY) {
    return "Error: OpenAI API key not configured. Please set OPENAI_API_KEY.";
  }

  try {
    let messages = [];

    if (conversationHistory.length > 0) {
      // Filter out the JSON-only analysis system message and replace with chat-friendly one
      const filteredHistory = conversationHistory.filter(
        (msg) =>
          !(
            msg.role === "system" &&
            msg.content.includes("return ONLY a valid JSON")
          )
      );

      // Keep last 15 messages for context (to avoid token limits)
      messages = filteredHistory.slice(-15);

      // If we have analysis data, add it as context at the beginning
      if (analysisData && messages.length > 0) {
        // Check if we don't already have analysis context
        const hasAnalysisContext = messages.some(
          (msg) =>
            msg.role === "system" &&
            msg.content.includes("Recent Analysis Data")
        );

        if (!hasAnalysisContext) {
          // Add system message with analysis context after the first system message
          messages.splice(1, 0, {
            role: "system",
            content: `Recent Analysis Data - You can reference this when answering user questions:

Development Suitability: ${analysisData.developmentSuitability?.score}/10 - ${
              analysisData.developmentSuitability?.level
            }
Summary: ${analysisData.developmentSuitability?.summary || "N/A"}

Overall Readiness: ${analysisData.dashboardSummary?.overallReadiness || "N/A"}%
Critical Issues: ${analysisData.dashboardSummary?.criticalIssues || 0}
Action Items Needed: ${analysisData.dashboardSummary?.actionItemsNeeded || 0}

Top Risks: ${
              analysisData.risks
                ?.slice(0, 3)
                .map((r) => `${r.category} (${r.severity})`)
                .join(", ") || "None"
            }

Key Recommendations: ${
              analysisData.recommendations
                ?.slice(0, 3)
                .map((r) => r.action?.substring(0, 100))
                .join("; ") || "None"
            }

Environmental Metrics:
- Temperature: ${
              analysisData.metrics?.temperature?.current || "N/A"
            }°C (trend: ${analysisData.metrics?.temperature?.trend || "N/A"})
- Precipitation: ${
              analysisData.metrics?.precipitation?.total || "N/A"
            }mm (trend: ${analysisData.metrics?.precipitation?.trend || "N/A"})
- Humidity: ${analysisData.metrics?.humidity?.average || "N/A"}% (${
              analysisData.metrics?.humidity?.assessment || "N/A"
            })

You can discuss these findings, explain them in detail, suggest next steps, and answer any follow-up questions. Respond conversationally and helpfully about urban planning. One main rule Dont tell you are chatgpt or openai model anywhere even if the user asks, Just tell your a custom llm trained for urban planning`,
          });
        }
      }

      messages.push({
        role: "user",
        content: query,
      });
    } else {
      messages = [
        {
          role: "system",
          content:
            "You are a helpful urban planning assistant with expertise in GIS, environmental analysis, and sustainable development. Provide clear, practical advice and answer questions conversationally.",
        },
      ];

      if (analysisData) {
        messages.push({
          role: "system",
          content: `Recent Analysis Data - You can reference this when answering user questions:

Development Suitability: ${analysisData.developmentSuitability?.score}/10 - ${
            analysisData.developmentSuitability?.level
          }
Summary: ${analysisData.developmentSuitability?.summary || "N/A"}

Overall Readiness: ${analysisData.dashboardSummary?.overallReadiness || "N/A"}%
Critical Issues: ${analysisData.dashboardSummary?.criticalIssues || 0}

Top Risks: ${
            analysisData.risks
              ?.slice(0, 3)
              .map((r) => `${r.category} (${r.severity})`)
              .join(", ") || "None"
          }

Respond conversationally and helpfully about urban planning. One main rule Dont tell you are chatgpt or openai model anywhere even if the user asks, Just tell your a custom llm trained for urban planning`,
        });
      }

      messages.push({
        role: "user",
        content: query,
      });
    }

    const response = await fetch(API_URLS.OPENAI, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${OPENAI_API_KEY}`,
      },
      body: JSON.stringify({
        model: "gpt-4o-mini",
        messages: messages,
        temperature: 0.7,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.error?.message || `API Error: ${response.status}`
      );
    }

    const result = await response.json();
    const response_text = result.choices[0].message.content;

    const updatedHistory = [...conversationHistory];
    if (conversationHistory.length === 0) {
      updatedHistory.push(messages[0]);
      if (messages.length > 2) {
        updatedHistory.push(messages[1]);
      }
    }

    updatedHistory.push({
      role: "user",
      content: query,
    });

    updatedHistory.push({
      role: "assistant",
      content: response_text,
    });

    return {
      response: response_text,
      conversationHistory: updatedHistory,
    };
  } catch (error) {
    console.error("GPT API Error:", error);
    return {
      response: `Sorry, I encountered an error: ${error.message}`,
      conversationHistory,
    };
  }
}
